import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { onboardingAPI, accountsAPI } from "../services/api";
import { useAuth } from "./AuthContext";
import { STEP_IDS, stepIndex, maxStepIndex, firstIncompleteStep } from "../lib/onboardingSteps";
import { toastFailure } from "../lib/toast";
import { retryDelay, FAILURES_BEFORE_RECONNECTING, onWakeOrOnline } from "../lib/retryLoader";

const OnboardingContext = createContext();

// Onboarding steps are tracked by NAME (see lib/onboardingSteps.js) so the
// branched archetype flows don't depend on a fixed count. TOTAL_STEPS is the
// canonical upper bound, used only as the progress-banner denominator;
// completion is driven by finishOnboarding() at the summary step.
const TOTAL_STEPS = STEP_IDS.length;
const FIRST_STEP = STEP_IDS[0];

export const OnboardingProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Resume pointer — the step name the user should land on. Derived from the
  // backend's completed_steps (first incomplete), never trusted from a count.
  const [currentStepId, setCurrentStepId] = useState(FIRST_STEP);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stravaConnected, setStravaConnected] = useState(false);
  // Tri-state: null until /onboarding/status answers (Layout shows nothing,
  // then "Reconnecting…", meanwhile). It used to default to true, and a failed
  // read set true too, so an athlete mid-onboarding whose status read failed
  // got the app shell (persona QA 2026-10-25).
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);
  // Consecutive failed status reads; retried with backoff while unknown.
  const [statusFailures, setStatusFailures] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Reset onboarding state whenever the authenticated user changes —
  // including the "logged out → logged in as someone else" path that
  // happens when an account is deleted and immediately rebuilt. Without
  // this, the new account inherits flags like `stravaConnected=true`
  // from the deleted account because the provider never unmounts, and
  // the Strava block's auto-advance fires off the stale value before
  // `fetchStatus()` has a chance to overwrite it.
  const lastUserKeyRef = useRef(null);
  useEffect(() => {
    const key = user?.id ?? user?.email ?? null;
    if (lastUserKeyRef.current === key) return;
    lastUserKeyRef.current = key;
    setCurrentStepId(FIRST_STEP);
    setCompletedSteps([]);
    setStravaConnected(false);
    setOnboardingCompleted(null);
    setStatusFailures(0);
    setIsLoading(Boolean(key));  // logged-out: no fetch coming, no spinner
    // If the user just logged in (vs. logged out), fetch the new identity's
    // real status. Layout's mount-effect handles the very first page load,
    // but won't re-fire if Layout stays mounted across an in-session
    // identity flip (delete account → register a new one).
    if (key && fetchStatusRef.current) {
      fetchStatusRef.current();
    }
  }, [user?.id, user?.email]);

  // Ref so the user-change effect above can call `fetchStatus` without
  // depending on the (not-yet-defined) function in its deps array.
  const fetchStatusRef = useRef(null);

  // Track the highest step (by canonical index) the user has visited so we can
  // let them navigate freely back and forward through anything they've seen.
  const maxVisitedIndex = Math.max(stepIndex(currentStepId), maxStepIndex(completedSteps));

  // Fetch onboarding status. While onboarding is incomplete, Layout redirects
  // every protected route to /onboarding — this just keeps that flag current;
  // it doesn't itself decide visibility anymore (routing does).
  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const status = await onboardingAPI.getStatus();
      if (typeof status?.onboarding_completed !== "boolean") throw new Error("onboarding status missing onboarding_completed");
      setOnboardingCompleted(status.onboarding_completed);
      setStatusFailures(0);

      // Backend's `current_step` is the most-recently-completed step name
      // (observability only). Don't trust it as the resume point — compute the
      // first step the user hasn't completed yet so they land where they left
      // off, branch-agnostically.
      const completed = Array.isArray(status.completed_steps) ? status.completed_steps : [];
      setCurrentStepId(firstIncompleteStep(completed));
      setCompletedSteps(completed);
      setStravaConnected(status.strava_connected);

      return status;
    } catch (error) {
      console.error("Failed to fetch onboarding status:", error);
      // Not an answer: keep what we knew (null stays unknown) and retry below.
      setStatusFailures((n) => n + 1);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // While the status is unknown and the last read failed: retry with backoff,
  // and at once when the tab comes back into view or the browser back online.
  const userKey = user?.id ?? user?.email ?? null;
  useEffect(() => {
    if (!userKey || onboardingCompleted !== null || statusFailures === 0) return undefined;
    const t = setTimeout(() => { fetchStatus(); }, retryDelay(statusFailures));
    const off = onWakeOrOnline(() => { fetchStatus(); });
    return () => { clearTimeout(t); off(); };
  }, [userKey, onboardingCompleted, statusFailures, fetchStatus]);
  const statusFailed = onboardingCompleted === null && statusFailures >= FAILURES_BEFORE_RECONNECTING;

  // Keep the ref in sync so the user-change effect can call the
  // latest stable `fetchStatus` without taking it as a dep.
  fetchStatusRef.current = fetchStatus;

  // Jump to a specific step. Refuses to move forward past the highest
  // step the user has already visited so they can't skip ahead into a
  // step they haven't seen — otherwise they could land on a step that
  // expects upstream answers.
  const goToStep = useCallback((stepId) => {
    const targetIdx = stepIndex(stepId);
    if (targetIdx < 0) return;
    setCurrentStepId((prev) => {
      const ceiling = Math.max(stepIndex(prev), maxStepIndex(completedSteps));
      if (targetIdx > ceiling) return prev;
      return stepId;
    });
  }, [completedSteps]);

  // Advance the resume pointer to the next canonical step after `stepId`, if
  // any. Completion is NOT triggered here — finishOnboarding() at the summary
  // step is the single completion path, which keeps the branched archetypes
  // (a coach never sees `interview`) finishing correctly.
  const advancePointer = useCallback((stepId) => {
    const idx = stepIndex(stepId);
    if (idx >= 0 && idx < STEP_IDS.length - 1) {
      setCurrentStepId(STEP_IDS[idx + 1]);
    }
  }, []);

  // Complete a step and move the resume pointer forward.
  const completeStep = useCallback(async (stepId) => {
    try {
      const result = await onboardingAPI.updateProgress(stepId, true);
      setCompletedSteps(result.completed_steps);

      // NOTE: `stravaConnected` is derived purely from the real connection
      // state (fetchStatus / checkStravaStatus / Strava OAuth return) — never
      // inferred from step progression.
      advancePointer(stepId);
    } catch (error) {
      toastFailure("Couldn't save that step just now — try again.", error, { log: "Failed to complete step:" });
    }
  }, [advancePointer]);

  // Skip a step (still records the resume position, not completion).
  const skipStep = useCallback(async (stepId) => {
    try {
      await onboardingAPI.updateProgress(stepId, false);
      advancePointer(stepId);
    } catch (error) {
      toastFailure("Couldn't skip that step just now — try again.", error, { log: "Failed to skip step:" });
    }
  }, [advancePointer]);

  // Finish onboarding (mark complete). The backend write is the single source
  // of truth for the gate, so a swallowed failure here strands the user behind
  // the /onboarding page forever. Retry once, and RE-THROW on failure so the
  // caller can surface an error + keep a retry button instead of navigating
  // away into a still-blocked app.
  // `to`: where the app goes next; null stays on the page (onboarding's first-plan screen).
  const finishOnboarding = useCallback(async (to = "/dashboard") => {
    let lastErr;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await onboardingAPI.complete();
        setOnboardingCompleted(true);
        if (to) navigate(to);
        return;
      } catch (error) {
        lastErr = error;
        console.error("Failed to complete onboarding (attempt %d):", attempt + 1, error);
      }
    }
    throw lastErr;
  }, [navigate]);


  // Check Strava connection status
  const checkStravaStatus = useCallback(async () => {
    try {
      const accounts = await accountsAPI.getLinkedAccounts();
      const strava = accounts.accounts?.find(a => a.provider === "strava");
      setStravaConnected(!!strava);
      return !!strava;
    } catch (error) {
      console.error("Failed to check Strava status:", error);
      return false;
    }
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        // State
        currentStepId,
        completedSteps,
        stravaConnected,
        onboardingCompleted,
        statusFailed,
        isLoading,
        totalSteps: TOTAL_STEPS,
        maxVisitedIndex,

        // Actions
        goToStep,
        completeStep,
        skipStep,
        finishOnboarding,
        fetchStatus,
        checkStravaStatus,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
};

export default OnboardingContext;
